import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  IconButton,
  MenuItem,
  InputLabel,
  Input,
  Checkbox,
  Tooltip,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PricingControlsTable from '~/components/AdminPortal/PricingControls/SharedTable';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import TruncatedText from '~/components/General/TruncatedText';
import '../styles.scss';
import './styles.scss';
import {
  fetchAdminPostageData,
  fetchAdminPostageHistoricalData,
  fetchAdminPostageById,
  createAdminPostage,
  updateAdminPostage,
  deleteAdminPostage,
} from '~/redux/actions/admin-pricing-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import useScreenWidth from '~/hooks/useScreenWidth';
import { balanceFormatter } from '~/utils/helperFunctions';
import { dateFormat, parseLocalDateOnly } from '~/utils/date-format';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import OLCBaseModal from '../BaseModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DoneIcon from '@mui/icons-material/Done';
import './AddPostageModal/styles.scss';
import {
  ContactDetailsDeleteCIcon,
  TrashTemplateCIcon,
  EditCampaignStepCIcon,
} from '~/assets/images';
import AddPostageModal from './AddPostageModal';
import EditPostageModal from './EditPostageModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import { MESSAGES } from '~/utils/message';

// Format date to YYYY-MM-DD format for API payloads
const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    // If it's already a string, extract just the date part
    return date.split('T')[0];
  }
  if (date instanceof Date) {
    // Format Date object to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

const buildColumns = (onOpenRowMenu, isWideScreen, activeRowId, menuOpen) => [
  {
    field: 'label',
    headerName: 'Postage Class',
    flex: isWideScreen ? 1 : 0,
    width: 220,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={params?.row?.label || ' -'} />
      </Box>
    ),
  },
  {
    field: 'cost',
    headerName: 'Postage Cost',
    flex: isWideScreen ? 1 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={balanceFormatter(params?.row?.cost, 3) || ''} />
      </Box>
    ),
  },
  {
    field: 'change',
    headerName: 'Change',
    flex: isWideScreen ? 1 : 0,
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText
          text={
            params?.row?.change == null
              ? 'Calculating...'
              : balanceFormatter(params?.row?.change, 3) || ''
          }
        />
      </Box>
    ),
  },
  {
    field: 'is_applicable',
    headerName: 'Update Pricing',
    flex: isWideScreen ? 1 : 0,
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={params?.row?.is_applicable === true ? 'Yes' : 'No'} />
      </Box>
    ),
  },
  {
    field: 'additional_increase',
    headerName: "Add'l Increase",
    flex: isWideScreen ? 1 : 0,
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={balanceFormatter(params?.row?.additional_increase, 3) || ''} />
      </Box>
    ),
  },
  {
    field: 'effective_date',
    headerName: 'Effective Date',
    flex: isWideScreen ? 1 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={dateFormat(params?.row?.effective_date, true, true) || ' -'} />
      </Box>
    ),
  },
  {
    field: 'status_label',
    headerName: 'Status',
    flex: isWideScreen ? 1 : 0,
    width: 140,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={params?.row?.status_label || ' -'} />
      </Box>
    ),
  },
  {
    field: 'action',
    headerName: 'Action',
    width: 100,
    sortable: false,
    renderCell: (params) => {
      const isActive = menuOpen && params.row.id === activeRowId;
      return (
        <Box>
          {params?.row?.status !== 'active' && (
            <IconButton
              disableRipple={true}
              onClick={(e) => onOpenRowMenu(e, params.row)}
              sx={{
                opacity: isActive ? 0.8 : 0.4,
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      );
    },
  },
];

const buildHistoricalColumns = (onOpenRowMenu, isWideScreen) => [
  {
    field: 'label',
    headerName: 'Postage Class',
    flex: isWideScreen ? 1 : 0,
    width: 220,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={params?.row?.label || ' -'} />
      </Box>
    ),
  },
  {
    field: 'cost',
    headerName: 'Postage Cost',
    flex: isWideScreen ? 1 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={balanceFormatter(params?.row?.cost, 3) || ''} />
      </Box>
    ),
  },
  {
    field: 'change',
    headerName: 'Change',
    flex: isWideScreen ? 1 : 0,
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText
          text={
            params?.row?.change == null
              ? 'Calculating...'
              : balanceFormatter(params?.row?.change, 3) || ''
          }
        />
      </Box>
    ),
  },
  {
    field: 'is_applicable',
    headerName: 'Update Pricing',
    flex: isWideScreen ? 1 : 0,
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={params?.row?.is_applicable === true ? 'Yes' : ''} />
      </Box>
    ),
  },
  {
    field: 'additional_increase',
    headerName: "Add'l Increase",
    flex: isWideScreen ? 1 : 0,
    width: 170,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={balanceFormatter(params?.row?.additional_increase, 3) || ''} />
      </Box>
    ),
  },
  {
    field: 'effective_date',
    headerName: 'Effective Date',
    flex: isWideScreen ? 1 : 0,
    width: 180,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={dateFormat(params?.row?.effective_date, true, true) || ' -'} />
      </Box>
    ),
  },
  {
    field: 'status_label',
    headerName: 'Status',
    flex: isWideScreen ? 1 : 0,
    width: 140,
    sortable: false,
    renderCell: (params) => (
      <Box className="addressWrapper">
        <TruncatedText text={params?.row?.status_label || ' -'} />
      </Box>
    ),
  },
];

const Postage = () => {
  const dispatch = useDispatch();
  const isWideScreen = useScreenWidth(1380);

  // Pagination state management (following Orders component pattern)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
  });
  const [historicalPagination, setHistoricalPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [historicalTableLoading, setHistoricalTableLoading] = useState(false);

  // Redux state
  const postageData = useSelector((state) => state.adminPricingReducers.postage?.current) || [];
  const historicalData =
    useSelector((state) => state.adminPricingReducers.postage?.historical) || [];
  const loading = useSelector((state) => state.adminPricingReducers.postage?.loading) || false;

  // Redux pagination data (like Orders component)
  const rowsCount = useSelector((state) => state.adminPricingReducers.postage?.rowsCount) || 0;
  const to = useSelector((state) => state.adminPricingReducers.postage?.to);
  const from = useSelector((state) => state.adminPricingReducers.postage?.from);
  const lastPage = useSelector((state) => state.adminPricingReducers.postage?.lastPage || 1);
  const currentPage = useSelector((state) => state.adminPricingReducers.postage?.currentPage || 1);
  const perPage = useSelector(
    (state) => state.adminPricingReducers.postage?.perPage || DEFAULT_PAGINATION.pageSize
  );
  const totalRecords = useSelector((state) => state.adminPricingReducers.postage?.totalRecordsInDB);

  // Historical Redux pagination data
  const historicalRowsCount =
    useSelector((state) => state.adminPricingReducers.postage?.historicalPagination?.rowsCount) ||
    0;
  const historicalTo = useSelector(
    (state) => state.adminPricingReducers.postage?.historicalPagination?.to
  );
  const historicalFrom = useSelector(
    (state) => state.adminPricingReducers.postage?.historicalPagination?.from
  );
  const historicalLastPage = useSelector(
    (state) => state.adminPricingReducers.postage?.historicalPagination?.lastPage || 1
  );
  const historicalCurrentPage = useSelector(
    (state) => state.adminPricingReducers.postage?.historicalPagination?.page || 1
  );
  const historicalPerPage = useSelector(
    (state) =>
      state.adminPricingReducers.postage?.historicalPagination?.pageSize ||
      DEFAULT_PAGINATION.pageSize
  );
  const historicalTotalRecords = useSelector(
    (state) => state.adminPricingReducers.postage?.historicalPagination?.totalRecordsInDB
  );

  const [addPostageOpen, setAddPostageOpen] = useState(false);
  const [editPostageOpen, setEditPostageOpen] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [postageForm, setPostageForm] = useState({
    postcard: '',
    letter: '',
    standard: '',
    forever: '',
    firstClassFlat: '',
    standardClassFlat: '',
    updatePostcard: false,
    updateLetter: false,
    updateStandard: false,
    updateForever: false,
    updateFirstClassFlat: false,
    updateStandardClassFlat: false,
    effectiveDate: null,
    notes: '',
  });
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({
    open: false,
    header: 'Delete Postage',
    message:
      'Are you sure you want to delete this postage? This will remove all postage associated with the same Effective Date.',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
  });
  const [confirmOverrideModal, setConfirmOverrideModal] = useState({
    open: false,
    header: 'Override Postage Version',
    message:
      'A Postage Version already exists for the selected Effective Date. Do you want to overwrite the existing Postage Version with this new one?',
    successButtonName: 'Yes',
    cancelButtonName: 'No',
    loading: false,
    icon: ContactDetailsDeleteCIcon,
    payloadData: null,
    handleClose: undefined,
    handleOk: undefined,
  });

  const shouldShowPagination = (totalRecords, pageSize = DEFAULT_PAGINATION.pageSize) => {
    const totalPages = Math.ceil(totalRecords / pageSize);

    return totalRecords >= pageSize || totalPages > 1;
  };

  const handleSavePostage = async (data, forceUpdate = false) => {
    setSaveLoading(true);
    try {
      const payload = {
        first_class_postcard_cost: +data.postcard,
        first_class_postcard_is_applicable: data.updatePostcard,
        first_class_postcard_additional_increase: +data.postcardIncrease,

        first_class_letter_cost: +data.letter,
        first_class_letter_is_applicable: data.updateLetter,
        first_class_letter_additional_increase: +data.letterIncrease,

        standard_class_cost: +data.standard,
        standard_class_is_applicable: data.updateStandard,
        standard_class_additional_increase: +data.standardIncrease,

        first_class_forever_cost: +data.forever,
        first_class_forever_is_applicable: data.updateForever,
        first_class_forever_additional_increase: +data.foreverIncrease,

        first_class_flat_cost: +data.firstClassFlat,
        first_class_flat_is_applicable: data.updateFirstClassFlat,
        first_class_flat_additional_increase: +data.firstClassFlatIncrease,

        standard_class_flat_cost: +data.standardClassFlat,
        standard_class_flat_is_applicable: data.updateStandardClassFlat,
        standard_class_flat_additional_increase: +data.standardClassFlatIncrease,

        effective_date: formatDateForAPI(data.effectiveDate),
        notes: data.notes,
        force_update: forceUpdate,
      };

      const response = await dispatch(createAdminPostage(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Postage created successfully'));
        setAddPostageOpen(false);
        if (response.data.data.show_override_modal) {
          const id = response.data.data.pricing_group_id;
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: true,
            payloadData: payload,
            handleOk: () => handleEditPostage(payload, true, id),
            handleClose: closeOverRideModal,
          }));
          return;
        }
        setPostageForm({
          postcard: '',
          letter: '',
          standard: '',
          forever: '',
          firstClassFlat: '',
          standardClassFlat: '',
          updatePostcard: false,
          updateLetter: false,
          updateStandard: false,
          updateForever: false,
          updateFirstClassFlat: false,
          updateStandardClassFlat: false,
          effectiveDate: null,
          notes: '',
        });
        // Refresh data
        getAdminPostage();
      } else if (response.status === 422) {
        dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
        if (response.data.data.show_override_modal) {
          const id = response.data.data.pricing_group_id;
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: true,
            payloadData: payload,
            handleOk: () => handleEditPostage(payload, true, id),
            handleClose: closeOverRideModal,
          }));
          return;
        }
      } else {
        dispatch(failure(response?.data?.message || 'Failed to create postage'));
      }
    } catch (error) {
      dispatch(
        failure(error?.response?.data?.message || error?.message || 'Failed to create postage')
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const openMenu = Boolean(menuAnchor);
  const handleOpenRowMenu = (event, row) => {
    setActiveRow(row);
    setMenuAnchor(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setActiveRow(null);
  };

  const closeOverRideModal = () => {
    setConfirmOverrideModal((prev) => ({ ...prev, open: false }));
    setPostageForm({
      postcard: '',
      letter: '',
      standard: '',
      forever: '',
      updatePostcard: false,
      updateLetter: false,
      updateStandard: false,
      updateForever: false,
      effectiveDate: null,
      notes: '',
    });
  };

  const prepareEditInitialValues = async (row) => {
    const base = {
      id: null,
      postcard: '',
      letter: '',
      standard: '',
      forever: '',
      updatePostcard: false,
      updateLetter: false,
      updateStandard: false,
      updateForever: false,
      effectiveDate: null,
      notes: '',
      postcardIncrease: '',
      letterIncrease: '',
      standardIncrease: '',
      foreverIncrease: '',
    };

    if (!row || !row.pricing_group_id) {
      return base;
    }

    try {
      // Call the API to get the full postage data
      const response = await dispatch(fetchAdminPostageById(row.pricing_group_id));

      if (response.status === 200 && response.data) {
        const data = response.data.data;

        const getPricingBySlug = (slug) => data?.pricings?.find((item) => item.slug === slug) || {};

        // Map the API response to form fields
        return {
          id: data?.id || data.pricings[0].pricing_group_id,
          postcard: getPricingBySlug('first_class_postcard').cost ?? '',
          letter: getPricingBySlug('first_class_letter').cost ?? '',
          standard: getPricingBySlug('standard_class').cost ?? '',
          forever: getPricingBySlug('first_class_forever').cost ?? '',
          firstClassFlat: getPricingBySlug('first_class_flat').cost ?? '',
          standardClassFlat: getPricingBySlug('standard_class_flat').cost ?? '',
          updatePostcard: getPricingBySlug('first_class_postcard').is_applicable ?? false,
          updateLetter: getPricingBySlug('first_class_letter').is_applicable ?? false,
          updateStandard: getPricingBySlug('standard_class').is_applicable ?? false,
          updateForever: getPricingBySlug('first_class_forever').is_applicable ?? false,
          updateFirstClassFlat: getPricingBySlug('first_class_flat').is_applicable ?? false,
          updateStandardClassFlat: getPricingBySlug('standard_class_flat').is_applicable ?? false,
          postcardIncrease: getPricingBySlug('first_class_postcard').additional_increase ?? '',
          letterIncrease: getPricingBySlug('first_class_letter').additional_increase ?? '',
          standardIncrease: getPricingBySlug('standard_class').additional_increase ?? '',
          foreverIncrease: getPricingBySlug('first_class_forever').additional_increase ?? '',
          firstClassFlatIncrease: getPricingBySlug('first_class_flat').additional_increase ?? '',
          standardClassFlatIncrease:
            getPricingBySlug('standard_class_flat').additional_increase ?? '',
          effectiveDate: data?.effective_date ? parseLocalDateOnly(data.effective_date) : null,
          notes: data?.notes ?? '',
          force_update: false,
        };
      } else {
        dispatch(failure('Failed to fetch postage details'));
        return base;
      }
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message || error?.message || 'Failed to fetch postage details'
        )
      );
      return base;
    }
  };

  // Fetch data functions
  const getAdminPostage = async () => {
    try {
      setTableLoading(true);
      const payload = {
        pagination,
      };
      await dispatch(fetchAdminPostageData(payload));
    } catch (error) {
      console.error('Failed to fetch postage data:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const getAdminPostageHistorical = async () => {
    try {
      setHistoricalTableLoading(true);
      const payload = {
        pagination: historicalPagination,
      };
      await dispatch(fetchAdminPostageHistoricalData(payload));
    } catch (error) {
      console.error('Failed to fetch historical postage data:', error);
    } finally {
      setHistoricalTableLoading(false);
    }
  };

  const columns = useMemo(
    () => buildColumns(handleOpenRowMenu, isWideScreen, activeRow?.id, openMenu),
    [handleOpenRowMenu, isWideScreen, activeRow?.id, openMenu]
  );
  const historicalColumns = useMemo(
    () => buildHistoricalColumns(handleOpenRowMenu, isWideScreen),
    [isWideScreen]
  );

  // Fetch data on component mount and when pagination changes
  useEffect(() => {
    getAdminPostage();
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    getAdminPostageHistorical();
  }, [historicalPagination.page, historicalPagination.pageSize]);

  // Current data pagination handlers (following Orders pattern)
  const updatePagination = (newPagination) => {
    setPagination(newPagination);
  };

  // Historical data pagination handlers
  const updateHistoricalPagination = (newPagination) => {
    setHistoricalPagination(newPagination);
  };

  // Handle edit and delete actions
  const handleEditPostage = async (values, forceUpdate = false, id = null) => {
    setSaveLoading(true);
    try {
      const payload = { ...values, force_update: forceUpdate };
      const response = await dispatch(
        updateAdminPostage({ ...payload, id: forceUpdate ? id : editInitialValues?.id })
      );
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Postage updated successfully'));
        if (forceUpdate) {
          setPostageForm({
            postcard: '',
            letter: '',
            standard: '',
            forever: '',
            firstClassFlat: '',
            standardClassFlat: '',
            updatePostcard: false,
            updateLetter: false,
            updateStandard: false,
            updateForever: false,
            updateFirstClassFlat: false,
            updateStandardClassFlat: false,
            effectiveDate: null,
            notes: '',
          });
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: false,
            payloadData: null,
            handleOk: () => {},
          }));
          setAddPostageOpen(false);
        }
        setEditPostageOpen(false);
        setEditInitialValues({});
        getAdminPostage();
      } else if (response.status === 422) {
        dispatch(failure(response.data.message || MESSAGES.GENERAL_ERROR));
        if (response.data.data.show_override_modal) {
          const id = response.data.data.pricing_group_id;
          setConfirmOverrideModal((prev) => ({
            ...prev,
            open: true,
            payloadData: payload,
            handleOk: () => handleEditPostage(payload, true, id),
            handleClose: closeOverRideModal,
          }));
          return;
        }
      } else {
        dispatch(failure(response?.data?.message || 'Failed to create postage'));
      }
    } catch (error) {
      dispatch(
        failure(error?.response?.data?.message || error?.message || 'Failed to update postage')
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeletePostage = async () => {
    try {
      const response = await dispatch(deleteAdminPostage(activeRow.pricing_group_id));
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Postage deleted successfully'));
        getAdminPostage();
      } else {
        dispatch(failure(response?.data?.message || 'Failed to delete postage'));
      }
    } catch (error) {
      dispatch(
        failure(error?.response?.data?.message || error?.message || 'Failed to delete postage')
      );
    }
  };

  return (
    <Container maxWidth="xxl" className="olcAdminSectionPage">
      <Box className="postageWrapper">
        <Box className="sectionHeader currentPostageHeader">
          <Box className="postageTableHeader">
            <Typography variant="h6" className="pricingControlsSectionTitle">
              Current USPS Postage
            </Typography>
          </Box>
          <OlcButton
            variant="primary"
            className="addPostageBtn"
            onClick={() => setAddPostageOpen(true)}
          >
            Add Postage
          </OlcButton>
        </Box>
        <PricingControlsTable
          className="pricing-controls-table"
          rows={postageData}
          getRowId={(row) => row?.id}
          columns={columns}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            lastPage: lastPage,
            total: rowsCount,
            from: from,
            to: to,
            totalRecords: totalRecords,
          }}
          updatePagination={updatePagination}
          showPagination={shouldShowPagination(totalRecords)}
          loading={tableLoading}
        />

        <Divider className="olcAdminSectionDivider" />
        <Box className="sectionHeader historicalHeader">
          <Typography variant="h6" className="pricingControlsSectionTitle">
            Historical USPS Postage
          </Typography>
        </Box>
        <PricingControlsTable
          className="pricing-controls-table"
          rows={historicalData}
          columns={historicalColumns}
          pagination={{
            page: historicalPagination.page,
            pageSize: historicalPagination.pageSize,
            lastPage: historicalLastPage,
            total: historicalRowsCount,
            from: historicalFrom,
            to: historicalTo,
            totalRecords: historicalTotalRecords,
          }}
          updatePagination={updateHistoricalPagination}
          showPagination={shouldShowPagination(historicalTotalRecords)}
          loading={historicalTableLoading}
        />
      </Box>

      <OlcActionMenu
        anchorEl={menuAnchor}
        open={openMenu}
        onClose={handleCloseMenu}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        disableScrollLock
      >
        <MenuItem
          onClick={async () => {
            try {
              handleCloseMenu();
              setEditLoading(true);

              const init = await prepareEditInitialValues(activeRow);
              setEditInitialValues(init);
              setEditPostageOpen(true);
            } catch (error) {
              dispatch(failure('Failed to load postage data for editing'));
              setEditPostageOpen(false);
            } finally {
              setEditLoading(false);
            }
          }}
        >
          <img src={EditCampaignStepCIcon} alt="edit" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmDeleteModal((m) => ({
              ...m,
              open: true,
              handleOk: async () => {
                setConfirmDeleteModal((mm) => ({ ...mm, loading: true }));
                await handleDeletePostage();
                setConfirmDeleteModal((mm) => ({ ...mm, loading: false, open: false }));
                handleCloseMenu();
              },
              handleClose: () => {
                setConfirmDeleteModal((mm) => ({ ...mm, open: false, loading: false }));
                handleCloseMenu();
              },
            }));
          }}
        >
          <img src={TrashTemplateCIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>

      <AddPostageModal
        open={addPostageOpen}
        handleClose={() => setAddPostageOpen(false)}
        handleSavePostage={handleSavePostage}
        loading={saveLoading}
      />

      <EditPostageModal
        open={editPostageOpen}
        handleClose={() => {
          setEditPostageOpen(false);
          setEditInitialValues({});
          setEditLoading(false);
        }}
        initialValues={editInitialValues}
        loading={saveLoading}
        onSubmit={async (vals, onClose) => {
          // Format values according to the submit payload structure
          const formattedPayload = {
            first_class_postcard_cost: +vals.postcard,
            first_class_postcard_is_applicable: vals.updatePostcard,
            first_class_postcard_additional_increase: +vals.postcardIncrease,

            first_class_letter_cost: +vals.letter,
            first_class_letter_is_applicable: vals.updateLetter,
            first_class_letter_additional_increase: +vals.letterIncrease,

            standard_class_cost: +vals.standard,
            standard_class_is_applicable: vals.updateStandard,
            standard_class_additional_increase: +vals.standardIncrease,

            first_class_forever_cost: +vals.forever,
            first_class_forever_is_applicable: vals.updateForever,
            first_class_forever_additional_increase: +vals.foreverIncrease,

            first_class_flat_cost: +vals.firstClassFlat,
            first_class_flat_is_applicable: vals.updateFirstClassFlat,
            first_class_flat_additional_increase: +vals.firstClassFlatIncrease,

            standard_class_flat_cost: +vals.standardClassFlat,
            standard_class_flat_is_applicable: vals.updateStandardClassFlat,
            standard_class_flat_additional_increase: +vals.standardClassFlatIncrease,

            effective_date: formatDateForAPI(vals.effectiveDate),
            notes: vals.notes,
            force_update: false,
          };

          await handleEditPostage(formattedPayload);
        }}
      />

      <ConfirmModal openConfirmModal={confirmDeleteModal} />
      <ConfirmModal openConfirmModal={confirmOverrideModal} />
    </Container>
  );
};

export default Postage;
